/**
 * 系统配置文件
 * 从统一配置文件中获取配置信息
 */

/* global wx */

// 检查是否在浏览器环境中
const isBrowser = typeof window !== 'undefined'

// 导入配置（只在 Node.js 环境中执行，浏览器环境从环境变量读取）
let config, ENV_CONFIG
if (!isBrowser) {
  // Node.js 环境（构建时）
  const envModule = require('../env.js')
  config = envModule.getConfig()
  ENV_CONFIG = envModule.ENV_CONFIG
} else {
  // 浏览器环境：从环境变量读取配置
  // 注意：这些值在构建时由 vue.config.js 注入
  const envConfig = process.env.VUE_APP_CONFIG ? JSON.parse(process.env.VUE_APP_CONFIG) : {}
  const workingTimeFromEnv = envConfig.WORKING_TIME || {}
  const workingTimeMessage =
    workingTimeFromEnv.WORKING_TIME_MESSAGE ||
    envConfig.WORKING_TIME_MESSAGE ||
    process.env.VUE_APP_WORKING_TIME_MESSAGE  

  config = {
    SYSTEM_NAME: envConfig.SYSTEM_NAME || process.env.VUE_APP_SYSTEM_NAME || '渤格循环科技',
    WECHAT_APP_ID: envConfig.WECHAT_APP_ID || process.env.VUE_APP_WECHAT_APP_ID || '',
    WECHAT_REDIRECT_URI: envConfig.WECHAT_REDIRECT_URI || '/login/callback',
    WECHAT_SCOPE: envConfig.WECHAT_SCOPE || 'snsapi_userinfo',
    WECHAT_STATE: envConfig.WECHAT_STATE || 'login',
    WORKING_TIME: envConfig.WORKING_TIME || {
      WORKING_DAYS: [1, 2, 3, 4, 5, 6],
      WORKING_START_HOUR: 10,
      WORKING_START_MINUTE: 0,
      WORKING_END_HOUR: 17,
      WORKING_END_MINUTE: 0,
      WORKING_TIME_MESSAGE: workingTimeMessage
    },
    DEV_MODE: envConfig.DEV_MODE || { ENABLED: false },
    WECHAT_JS_SDK: envConfig.WECHAT_JS_SDK || {}
  }
  ENV_CONFIG = {
    development: { API_URL: envConfig.API_URL_DEV || '' },
    production: { API_URL: envConfig.API_URL_PROD || '' }
  }
}

// 导入微信工具方法
const { buildWeChatJSSDKConfig, validateWeChatConfig } = require('./utils/wechatUtils.js')

// 从统一配置获取系统信息
export const SYSTEM_NAME = config.SYSTEM_NAME
export const WECHAT_APP_ID = config.WECHAT_APP_ID

// API接口地址配置
export const API_URL = {
  // 开发环境
  dev: ENV_CONFIG.development.API_URL,
  // 生产环境
  production: ENV_CONFIG.production.API_URL
}

// 微信配置
export const WECHAT_CONFIG = {
  APP_ID: config.WECHAT_APP_ID,
  REDIRECT_URI: config.WECHAT_REDIRECT_URI,
  SCOPE: config.WECHAT_SCOPE,
  STATE: config.WECHAT_STATE
}

// 客服配置
export const CUSTOMER_SERVICE = {
  // 客服工号
  SERVICE_NUMBER: '0001',
  // 客服电话
  PHONE_NUMBER: '19980859372'
}

// 认证相关提示信息
export const CERTIFICATION_MESSAGES = {
  // 未完成认证提示
  NOT_CERTIFIED: '您还未完成实名认证，请立即认证',
  // 未完成认证标题
  NOT_CERTIFIED_TITLE: '未完成认证',
  // 需要认证才能卖金提示
  NEED_CERTIFICATION_FOR_SELL: '请先完成实名认证后再进行卖金操作'
}

// 工作时间配置
export const WORKING_TIME = config.WORKING_TIME || {
  WORKING_DAYS: [1, 2, 3, 4, 5, 6],
  WORKING_START_HOUR: 10,
  WORKING_START_MINUTE: 0,
  WORKING_END_HOUR: 17,
  WORKING_END_MINUTE: 0,
  WORKING_TIME_MESSAGE: config.WORKING_TIME_MESSAGE 
}

// 开发模式配置
export const DEV_MODE = config.DEV_MODE || {
  ENABLED: false
}

// 根据环境获取API地址
export const getApiUrl = () => {
  return process.env.NODE_ENV === 'production' ? API_URL.production : API_URL.dev
}

// 获取前端访问地址
export const getFrontendUrl = () => {
  return window.location.origin
}

// 生成微信网页授权链接（公众号 H5）
export const buildWeChatOAuthUrl = ({
  appId = WECHAT_CONFIG.APP_ID,
  redirectUri = WECHAT_CONFIG.REDIRECT_URI,
  scope = WECHAT_CONFIG.SCOPE,
  state = WECHAT_CONFIG.STATE
} = {}) => {
  // 确保redirectUri正确进行URL编码
  const encodedRedirect = encodeURIComponent(redirectUri)
  return `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${encodedRedirect}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`
}

// 微信JS-SDK配置
export const initWeChatJSSDK = () => {
  try {
    // 检查是否在微信环境中
    if (typeof wx === 'undefined') {
      return false;
    }

    // 验证配置完整性（不阻断页面运行，仅告警）
    try {
      if (!validateWeChatConfig(config)) {
        // 配置不完整，已跳过
      }
    } catch (e) {
      // 配置校验异常，继续初始化
    }

    // 直接从env.js配置中获取微信JS-SDK配置
    const wxConfig = buildWeChatJSSDKConfig(config);
    
    // 配置微信JS-SDK
    wx.config(wxConfig);

    // 配置成功后的回调
    wx.ready(() => {
      // 微信JS-SDK配置成功
    });

    // 配置失败的回调
    wx.error(() => {
      // 不抛出异常，避免页面刷新
    });

    return true;
  } catch (error) {
    return false;
  }
}

// 增强的微信JS-SDK初始化方法（带重试机制）
export const initWeChatJSSDKWithRetry = (maxRetries = 3) => {
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const result = initWeChatJSSDK();
      if (result) {
        return result;
      }
      
      retryCount++;
      if (retryCount < maxRetries) {
        // 微信JS-SDK初始化失败，正在重试
        // 等待一段时间后重试
        setTimeout(() => {}, 1000 * retryCount);
      }
    } catch (error) {
      retryCount++;
      
      if (retryCount >= maxRetries) {
        return false;
      }
      
      // 等待一段时间后重试
      setTimeout(() => {}, 1000 * retryCount);
    }
  }
  
  return false;
}

// 使用微信JS-SDK登录
export const wechatJSSDKLogin = () => {
  return new Promise((resolve, reject) => {
    wx.login({
      success: (res) => {
        if (res.code) {
          resolve(res.code);
        } else {
          reject(new Error('获取微信登录code失败'));
        }
      },
      fail: (err) => {
        reject(err);
      }
    });
  });
}

// 导出所有配置
export default {
  SYSTEM_NAME,
  API_URL,
  WECHAT_CONFIG,
  CUSTOMER_SERVICE,
  CERTIFICATION_MESSAGES,
  WORKING_TIME,
  DEV_MODE,
  getApiUrl,
  getFrontendUrl,
  buildWeChatOAuthUrl,
  initWeChatJSSDK,
  initWeChatJSSDKWithRetry,
  wechatJSSDKLogin
}


